import Spacer from "@/components/atoms/utilities/Spacer";
import { css } from "@emotion/react";
import { PageTitle } from "@/components/molecules";
import { MainTemplate } from "@/components/templates";
import { EditCompanyButton } from "@/components/atoms/EditCompanyButton";
import { color } from "@/styles/theme";
import { useIsSpMode } from "@/hooks";
import React, { useContext, useEffect, useState } from "react";
import { Font } from "@moneyforward/mfx_biz_react_components";
import {
  Company,
  useGetBankBranchOfficesLazyQuery,
  useGetCompanyInfoLazyQuery,
} from "@/generated/graphql";
import { AuthContext, AuthStatus } from "@/contexts/AuthContext";
import { CompanyInfoLabel } from "@/components/molecules/CompanyInfoLabel";
import {
  COMPANY_OPTIONS_INDUSTRIES_CODE_LARGE,
  COMPANY_OPTIONS_INDUSTRIES_CODE_MIDDLE_RAW,
  COMPANY_OPTIONS_PREFECTURES,
  COMPANY_OPTIONS_SETTLEMENT_MONTHS,
  COMPANY_OPTIONS_STAFF_COUNTS,
} from "@/libs/constants";
import { SiteUrlLabel } from "@/components/molecules/SiteUrlLabel";
import useRollbarUtil from "../hooks/useRollbarUtil";
import config from "@/libs/config";
import { fmtDate, fmtBusinessType } from "@/libs/util";

type Props = {};

export const ManageCompanyPage = (props: Props) => {
  const { getNotifyErrorHandlerForGraphQL } = useRollbarUtil();
  const { authStatus } = useContext(AuthContext);
  const isSpMode = useIsSpMode();
  const [company, setCompany] = useState<Company>();
  const [bankBranchOfficesOptions, setBankBranchOfficesOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const [getCompanyInfoQuery, { loading: getCompanyInfoLoading }] =
    useGetCompanyInfoLazyQuery({
      onCompleted: (data) => {
        if (data?.company) {
          setCompany(data?.company as Company);
        }
      },
      onError: getNotifyErrorHandlerForGraphQL("Failed to get company info"),
    });

  const [getBankBranchOfficesQuery, { loading: getBankBranchOfficesLoading }] =
    useGetBankBranchOfficesLazyQuery({
      onCompleted: (data) => {
        const bankBranchOfficesOptions = data.bankBranchOffices.map((v) => {
          const label = v.branchOfficeName
            ? `${v.branchOfficeName}（${v.branchOfficeCode}）`
            : v.branchOfficeCode;
          return { label: label, value: v.branchOfficeId.toString() };
        });
        setBankBranchOfficesOptions(bankBranchOfficesOptions);
      },
      onError: getNotifyErrorHandlerForGraphQL(
        "Failed to get bank branch offices"
      ),
    });

  useEffect(() => {
    if (authStatus === AuthStatus.SignedIn) {
      getCompanyInfoQuery();
      config.IS_INDUSTRY_TYPE_BANK && getBankBranchOfficesQuery();
    }
  }, [authStatus]);

  const findOptionLabel = (
    options: { label: string; value: string }[],
    value: string,
    suffix: string = ""
  ) => {
    const label = options.find((o) => o.value === value)?.label;
    return label && label + suffix;
  };

  const showCapital = (capital: number): string => {
    // 金額の微調整
    let capitalArranged = capital === 0 ? 0 : Math.round(capital / 100);
    capitalArranged = capitalArranged === 0 ? 0 : capitalArranged / 100; // 小数点以下２番目まで
    return capitalArranged.toLocaleString() + "万円";
  };

  return (
    <MainTemplate title={`事業者管理`}>
      {/*See comment in MainTemplate.tsx - contentInner*/}
      {/*<Spacer size={56} />*/}
      <PageTitle mainTitle={`事業者管理`} />
      {getCompanyInfoLoading || getBankBranchOfficesLoading ? (
        <p>loading...</p>
      ) : (
        <div css={container}>
          <div css={!isSpMode ? header : spModeHeader}>
            <Font>事業者ID: {company?.displayCompanyId}</Font>
            <EditCompanyButton />
          </div>

          <div css={!isSpMode ? box : spModeBox}>
            <div css={sectionContainer}>
              <Font variant="mb">基本情報</Font>
              <CompanyInfoLabel
                name={"事業形態"}
                value={fmtBusinessType(company?.companyBusinessType)}
              />
              <CompanyInfoLabel
                name={"事業者名"}
                value={company?.companyNameStoreName}
              />
              <CompanyInfoLabel
                name={"事業者名(カナ)"}
                value={company?.companyNameStoreNameKana}
              />
              <CompanyInfoLabel
                name={"法人番号"}
                value={company?.corporateNumber}
              />
              <CompanyInfoLabel
                name={"適格請求書発行事業者 登録番号"}
                value={
                  company?.qualifiedInvoiceIssuerRegistrationNumber
                    ? company?.qualifiedInvoiceIssuerRegistrationNumber
                    : null
                }
              />
              <CompanyInfoLabel
                name={"業種(大分類)"}
                value={findOptionLabel(
                  COMPANY_OPTIONS_INDUSTRIES_CODE_LARGE,
                  company?.industriesLarge
                )}
              />
              <CompanyInfoLabel
                name={"業種(中分類)"}
                value={
                  company?.industriesLarge &&
                  company?.industriesMiddle &&
                  COMPANY_OPTIONS_INDUSTRIES_CODE_MIDDLE_RAW[
                    company?.industriesLarge
                  ][company?.industriesMiddle]
                }
              />
              <CompanyInfoLabel
                name={"事業内容"}
                value={company?.businessContent}
              />
              <CompanyInfoLabel
                name={"設立年月日"}
                value={
                  company?.foundationDate && fmtDate(company?.foundationDate)
                }
              />
              <CompanyInfoLabel
                name={"資本金"}
                value={
                  typeof company?.capital === "number"
                    ? showCapital(company?.capital!!)
                    : null
                }
              />
              <CompanyInfoLabel
                name={"従業員数"}
                value={findOptionLabel(
                  COMPANY_OPTIONS_STAFF_COUNTS,
                  company?.staffCount,
                  "名"
                )}
              />
              <CompanyInfoLabel
                name={"決算月"}
                value={findOptionLabel(
                  COMPANY_OPTIONS_SETTLEMENT_MONTHS,
                  company?.settlementMonth,
                  "月"
                )}
              />
              <SiteUrlLabel name={"ホームページ"} value={company?.siteUrl} />
              {config.IS_INDUSTRY_TYPE_BANK && (
                <CompanyInfoLabel
                  name={"取引支店"}
                  value={findOptionLabel(
                    bankBranchOfficesOptions,
                    company?.branchOfficeId?.toString()
                  )}
                />
              )}
            </div>

            <div css={sectionContainer}>
              <Font variant="mb">住所</Font>
              <CompanyInfoLabel
                name={"郵便番号"}
                value={
                  company?.postalCode &&
                  company?.postalCode.substring(0, 3) +
                    "-" +
                    company?.postalCode.substring(3, 7)
                }
              />
              <CompanyInfoLabel
                name={"都道府県"}
                value={findOptionLabel(
                  COMPANY_OPTIONS_PREFECTURES,
                  company?.prefectures
                )}
              />
              <CompanyInfoLabel
                name={"市区町村"}
                value={company?.municipality}
              />
              <CompanyInfoLabel
                name={"番地・ビル名"}
                value={company?.blockBuildingName}
              />
            </div>

            <div css={sectionContainer}>
              <Font variant="mb">代表者の情報</Font>
              <CompanyInfoLabel
                name={"代表者名"}
                value={company?.representativeName}
              />
              <CompanyInfoLabel
                name={"代表者 生年月日"}
                value={fmtDate(company?.representativeBirthDate)}
              />
              <CompanyInfoLabel
                name={"代表電話番号"}
                value={company?.phoneNumber}
              />
              <CompanyInfoLabel
                name={"代表メールアドレス"}
                value={company?.representativeEmailAddress}
              />
            </div>

            <div css={sectionContainer}>
              <Font variant="mb">Mikatano シリーズの主な利用者</Font>
              <CompanyInfoLabel
                name={"利用者名"}
                value={company?.mainUserName}
              />
              <CompanyInfoLabel
                name={"電話番号"}
                value={company?.mainUserPhoneNumber}
              />
            </div>

            {config.IS_INDUSTRY_TYPE_INSURANCE && (
              <div css={sectionContainer}>
                <Font variant="mb">その他</Font>
                <CompanyInfoLabel
                  name={"代理店コード"}
                  value={company?.insuranceAgencyCode}
                />
                <CompanyInfoLabel
                  name={"代理店名"}
                  value={company?.insuranceAgencyName}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </MainTemplate>
  );
};

const container = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const header = css({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  width: "640px",
  marginBottom: "8px",
});

const spModeHeader = css({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  width: "100%",
  marginBottom: "8px",
});

const box = css({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  width: "640px",
  padding: "32px",
  border: `1px solid ${color.iron}`,
  borderRadius: "4px",
});

const spModeBox = css({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  width: "100%",
  padding: "15px 17px",
  border: `1px solid ${color.iron}`,
  borderRadius: "4px",
});

const sectionContainer = css({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});
